import type { CaptureResult } from 'posthog-js'

import {
  isProductEventName,
  projectProductEvent,
} from '@scrambl/shared/product-analytics'

import {
  projectAnalyticsPathname,
  projectAnalyticsUrl,
} from './analytics-routes'

const UUID = /^[\da-f]{8}-[\da-f]{4}-[\da-f]{4}-[\da-f]{4}-[\da-f]{12}$/i
const ROLES = ['USER', 'ADMIN', 'SUPER_ADMIN', 'OWNER', 'MEMBER', 'VIEWER']
const URL_KEYS = [
  '$current_url',
  '$referrer',
  '$initial_current_url',
  '$initial_referrer',
  '$prev_pageview_url',
]
const PATH_KEYS = ['$pathname', '$initial_pathname', '$prev_pageview_pathname']
const CONTROL_EVENTS = new Set([
  '$pageview',
  '$pageleave',
  '$identify',
  '$groupidentify',
  '$set',
  '$create_alias',
  '$feature_flag_called',
  '$opt_in',
])
function read(value: unknown, key: string): unknown {
  if (!value || typeof value !== 'object') return undefined
  try {
    const descriptor = Object.getOwnPropertyDescriptor(value, key)
    return descriptor && 'value' in descriptor ? descriptor.value : undefined
  } catch {
    return undefined
  }
}
function uuid(value: unknown): string | undefined {
  return typeof value === 'string' && UUID.test(value) ? value : undefined
}
function profile(value: unknown, appUrl: string): Record<string, unknown> {
  const output: Record<string, unknown> = {}
  const role = read(value, 'role')
  if (typeof role === 'string' && ROLES.includes(role)) output.role = role
  for (const key of URL_KEYS) {
    const valueForKey = read(value, key)
    if (typeof valueForKey === 'string')
      output[key] = projectAnalyticsUrl(valueForKey, appUrl)
  }
  for (const key of PATH_KEYS) {
    const valueForKey = read(value, key)
    if (typeof valueForKey === 'string')
      output[key] = projectAnalyticsPathname(
        valueForKey.split(/[?#]/)[0] ?? '/',
      )
  }
  for (const key of ['$referring_domain', '$initial_referring_domain']) {
    const domain = read(value, key)
    if (typeof domain === 'string') output[key] = domain ? 'external' : 'direct'
  }
  return output
}
function buildContext(value: Record<string, unknown>): Record<string, string> {
  const output: Record<string, string> = {}
  for (const [key, choices] of Object.entries({
    app_platform: ['web', 'desktop'],
    app_runtime: ['browser', 'tauri'],
    app_environment: [
      'development',
      'production',
      'staging',
      'test',
      'dev',
      'prod',
    ],
  })) {
    const item = read(value, key)
    if (typeof item === 'string' && choices.includes(item)) output[key] = item
  }
  const version = read(value, 'app_version')
  if (
    typeof version === 'string' &&
    /^\d+\.\d+\.\d+(?:[-+][a-zA-Z0-9.-]+)?$/.test(version) &&
    version.length <= 80
  )
    output.app_version = version
  return output
}
function metadata(value: unknown, appUrl: string): Record<string, unknown> {
  const output = profile(value, appUrl)
  for (const key of [
    'distinct_id',
    '$anon_distinct_id',
    '$device_id',
    '$session_id',
    '$window_id',
    '$pageview_id',
    '$group_key',
    'alias',
    'original',
  ]) {
    const id = uuid(read(value, key))
    if (id) output[key] = id
  }
  const organization = uuid(read(read(value, '$groups'), 'organization'))
  if (organization) output.$groups = { organization }
  for (const key of [
    '$screen_height',
    '$screen_width',
    '$viewport_height',
    '$viewport_width',
    '$duration',
    '$prev_pageview_duration',
    '$prev_pageview_height',
    '$prev_pageview_max_content_percentage',
    '$prev_pageview_max_scroll_percentage',
  ]) {
    const number = read(value, key)
    if (
      typeof number === 'number' &&
      Number.isFinite(number) &&
      number >= 0 &&
      number <= 1_000_000_000
    )
      output[key] = number
  }
  for (const key of ['$is_identified', '$process_person_profile']) {
    const boolean = read(value, key)
    if (typeof boolean === 'boolean') output[key] = boolean
  }
  for (const [key, choices] of Object.entries({
    $browser: [
      'Chrome',
      'Firefox',
      'Safari',
      'Microsoft Edge',
      'Edge',
      'Opera',
      'Internet Explorer',
      'Mobile Safari',
      'Android Mobile',
      'Chrome iOS',
      'Firefox iOS',
      'Samsung Internet',
      'UC Browser',
    ],
    $os: ['Windows', 'Mac OS X', 'Linux', 'iOS', 'Android', 'Chrome OS'],
    $device_type: ['Desktop', 'Mobile', 'Tablet'],
    $group_type: ['organization'],
  })) {
    const item = read(value, key)
    if (typeof item === 'string' && choices.includes(item)) output[key] = item
  }
  for (const key of ['$browser_version', '$os_version']) {
    const version = read(value, key)
    if (
      typeof version === 'number' &&
      Number.isFinite(version) &&
      version >= 0 &&
      version <= 10_000
    )
      output[key] = version
    else if (
      typeof version === 'string' &&
      /^\d+(?:\.\d+){0,3}$/.test(version) &&
      version.length <= 32
    )
      output[key] = version
  }
  // These identifiers are vendor-public flag dimensions, not record strings.
  const flag = read(value, '$feature_flag')
  if (typeof flag === 'string' && /^[a-zA-Z0-9_-]{1,80}$/.test(flag))
    output.$feature_flag = flag
  const response = read(value, '$feature_flag_response')
  if (
    typeof response === 'boolean' ||
    (typeof response === 'string' && /^[a-zA-Z0-9_-]{1,80}$/.test(response))
  )
    output.$feature_flag_response = response
  if (read(value, '$lib') === 'web') output.$lib = 'web'
  const version = read(value, '$lib_version')
  if (
    typeof version === 'string' &&
    /^\d+\.\d+\.\d+(?:-[a-z0-9.-]+)?$/i.test(version) &&
    version.length <= 64
  )
    output.$lib_version = version
  return output
}

/** Project custom events after SDK/persistence enrichment. SDK recording and
 * autocapture protocols remain separate: do not strip their protocol payloads
 * into empty recordings or pretend this hook proves all replay serialization.
 */
export function projectPosthogEvent(
  event: CaptureResult | null,
  appUrl: string,
  superProperties: Record<string, unknown>,
): CaptureResult | null {
  if (!event) return null
  const custom = projectProductEvent(event.event, event.properties)
  if (isProductEventName(event.event) && !custom) return null
  const controlled = !!custom || CONTROL_EVENTS.has(event.event)
  const properties: Record<string, unknown> = controlled
    ? {
        ...metadata(event.properties, appUrl),
        ...custom?.properties,
        ...buildContext(superProperties),
      }
    : { ...event.properties }
  if (controlled) {
    if (read(event.properties, '$title') !== undefined)
      properties.$title = 'Scrambl'
    for (const key of ['$set', '$set_once', '$group_set']) {
      const bag = read(event.properties, key)
      if (bag !== undefined) properties[key] = profile(bag, appUrl)
    }
  } else {
    for (const key of Object.keys(properties)) {
      if (
        /(url|referrer|href)$/i.test(key) &&
        typeof properties[key] === 'string'
      )
        properties[key] = projectAnalyticsUrl(properties[key], appUrl)
    }
    for (const key of PATH_KEYS) {
      if (typeof properties[key] === 'string')
        properties[key] = projectAnalyticsPathname(
          properties[key].split(/[?#]/)[0] ?? '/',
        )
    }
    if ('$title' in properties) properties.$title = 'Scrambl'
    for (const key of ['$referring_domain', '$initial_referring_domain']) {
      if (key in properties) properties[key] = 'external'
    }
  }
  for (const key of ['$set', '$set_once', '$group_set']) {
    const bag = read(properties, key)
    if (bag !== undefined) properties[key] = profile(bag, appUrl)
  }
  // CaptureResult's person bags are siblings of properties in this SDK.
  // Project BOTH locations; changing only properties.$set misses identify.
  return {
    ...event,
    properties,
    ...(event.$set === undefined ? {} : { $set: profile(event.$set, appUrl) }),
    ...(event.$set_once === undefined
      ? {}
      : { $set_once: profile(event.$set_once, appUrl) }),
  }
}
